// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Copyright (c) 2026 Miskulin Technologies. All rights reserved.
// ================================================================
import React, { useState } from 'react';
import { useAuth } from '../AuthContext';

const CAMPAIGN_TYPES = [
  { id: 'welcome', name: 'Welcome Series', icon: '\uD83D\uDC4B', desc: 'Automated sequence for new subscribers' },
  { id: 'newsletter', name: 'Newsletter', icon: '\uD83D\uDCF0', desc: 'Regular updates to your audience' },
  { id: 'promo', name: 'Promotional', icon: '\uD83C\uDF89', desc: 'Sales, discounts, and special offers' },
  { id: 'reengage', name: 'Re-engagement', icon: '\uD83D\uDD04', desc: 'Win back inactive subscribers' },
  { id: 'cart', name: 'Abandoned Cart', icon: '\uD83D\uDED2', desc: 'Recover lost sales automatically' },
  { id: 'launch', name: 'Product Launch', icon: '\uD83D\uDE80', desc: 'Announce new products or services' },
  { id: 'event', name: 'Event Invite', icon: '\uD83C\uDF9F\uFE0F', desc: 'Webinars, workshops, and events' },
];

const CONTENT_BLOCKS = [
  { id: 'header', label: 'Header', icon: '\uD83C\uDFF7\uFE0F' },
  { id: 'text', label: 'Text Block', icon: '\uD83D\uDCDD' },
  { id: 'image', label: 'Image', icon: '\uD83D\uDDBC\uFE0F' },
  { id: 'button', label: 'Button', icon: '\uD83D\uDD18' },
  { id: 'divider', label: 'Divider', icon: '\u2796' },
  { id: 'social', label: 'Social Links', icon: '\uD83D\uDD17' },
  { id: 'footer', label: 'Footer', icon: '\uD83D\uDCCE' },
];

const PROVIDERS = [
  { id: 'mailchimp', name: 'Mailchimp', status: 'available' },
  { id: 'sendgrid', name: 'SendGrid', status: 'available' },
  { id: 'brevo', name: 'Brevo', status: 'available' },
];

const DEMO_CAMPAIGNS = [
  { id: 1, subject: 'Welcome to Our Community!', type: 'Welcome', sent: 450, opened: 312, clicked: 89, openRate: '69.3%', clickRate: '19.8%', status: 'Sent', date: 'Feb 15, 2026' },
  { id: 2, subject: 'February Newsletter - What\'s New', type: 'Newsletter', sent: 1200, opened: 408, clicked: 96, openRate: '34.0%', clickRate: '8.0%', status: 'Sent', date: 'Feb 12, 2026' },
  { id: 3, subject: 'Flash Sale - 40% Off Everything', type: 'Promo', sent: 0, opened: 0, clicked: 0, openRate: '-', clickRate: '-', status: 'Draft', date: 'Feb 18, 2026' },
  { id: 4, subject: 'We Miss You! Come Back for 20% Off', type: 'Re-engage', sent: 320, opened: 96, clicked: 28, openRate: '30.0%', clickRate: '8.8%', status: 'Sent', date: 'Feb 8, 2026' },
];

const AUTOMATION_FLOWS = [
  { id: 1, name: 'Welcome Sequence', trigger: 'New subscriber', emails: 3, status: 'Active', sent: 1240 },
  { id: 2, name: 'Cart Recovery', trigger: 'Cart abandoned', emails: 2, status: 'Active', sent: 89 },
  { id: 3, name: 'Re-engagement', trigger: '30 days inactive', emails: 2, status: 'Paused', sent: 320 },
];

function EmailMarketingPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('campaigns');
  const [selectedType, setSelectedType] = useState(null);
  const [emailBlocks, setEmailBlocks] = useState(['header', 'text', 'image', 'button', 'footer']);
  const [subject, setSubject] = useState('');
  const [previewText, setPreviewText] = useState('');

  const stats = [
    { label: 'Total Subscribers', value: '2,847', change: '+124 this month', color: '#6366f1' },
    { label: 'Avg Open Rate', value: '38.2%', change: '+2.1% vs last month', color: '#10b981' },
    { label: 'Avg Click Rate', value: '12.4%', change: '+0.8% vs last month', color: '#3b82f6' },
    { label: 'Unsubscribe Rate', value: '0.3%', change: '-0.1% vs last month', color: '#f59e0b' },
  ];

  const addBlock = (blockId) => {
    setEmailBlocks(prev => [...prev, blockId]);
  };

  const removeBlock = (index) => {
    setEmailBlocks(prev => prev.filter((_, i) => i !== index));
  };

  const renderStats = () => (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16, marginBottom: 28 }}>
      {stats.map(s => (
        <div key={s.label} style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: 20 }}>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 4 }}>{s.label}</p>
          <p style={{ fontSize: 28, fontWeight: 700, color: s.color }}>{s.value}</p>
          <p style={{ fontSize: 12, color: '#10b981', marginTop: 4 }}>{s.change}</p>
        </div>
      ))}
    </div>
  );

  const renderCampaigns = () => (
    <div>
      {renderStats()}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ fontSize: 20, fontWeight: 600, color: 'var(--text)' }}>Campaigns</h2>
        <button onClick={() => setTab('editor')} style={{ padding: '8px 20px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>+ New Campaign</button>
      </div>
      <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2.5fr 100px 80px 80px 80px 80px 80px 80px 120px', padding: '12px 20px', background: 'var(--bg)', borderBottom: '1px solid var(--border)', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
          <span>Subject</span><span>Type</span><span>Status</span><span>Sent</span><span>Opened</span><span>Clicked</span><span>Open %</span><span>Click %</span><span>Date</span>
        </div>
        {DEMO_CAMPAIGNS.map(c => (
          <div key={c.id} style={{ display: 'grid', gridTemplateColumns: '2.5fr 100px 80px 80px 80px 80px 80px 80px 120px', padding: '14px 20px', borderBottom: '1px solid var(--border)', alignItems: 'center', fontSize: 14 }}>
            <span style={{ fontWeight: 500, color: 'var(--text)' }}>{c.subject}</span>
            <span style={{ fontSize: 12, padding: '2px 8px', background: 'var(--bg)', borderRadius: 4, color: 'var(--text-muted)' }}>{c.type}</span>
            <span style={{ fontSize: 13, fontWeight: 500, color: c.status === 'Sent' ? '#10b981' : '#f59e0b' }}>{c.status}</span>
            <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>{c.sent.toLocaleString()}</span>
            <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>{c.opened.toLocaleString()}</span>
            <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>{c.clicked}</span>
            <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>{c.openRate}</span>
            <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>{c.clickRate}</span>
            <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>{c.date}</span>
          </div>
        ))}
      </div>
    </div>
  );

  const renderEditor = () => (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 600, color: 'var(--text)', marginBottom: 16 }}>Create Campaign</h2>
      {!selectedType ? (
        <div>
          <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 16 }}>Choose a campaign type to get started.</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
            {CAMPAIGN_TYPES.map(t => (
              <div key={t.id} onClick={() => setSelectedType(t.id)} style={{ padding: 20, background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, cursor: 'pointer', textAlign: 'center' }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>{t.icon}</div>
                <h4 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>{t.name}</h4>
                <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 20 }}>
          <div>
            <h3 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', marginBottom: 12 }}>Content Blocks</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {CONTENT_BLOCKS.map(b => (
                <button key={b.id} onClick={() => addBlock(b.id)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, cursor: 'pointer', color: 'var(--text)', fontSize: 14, textAlign: 'left' }}>
                  <span>{b.icon}</span> {b.label} <span style={{ marginLeft: 'auto', color: 'var(--text-muted)', fontSize: 12 }}>+</span>
                </button>
              ))}
            </div>
            <div style={{ marginTop: 20 }}>
              <label style={labelStyle}>Subject Line</label>
              <input type="text" value={subject} onChange={e => setSubject(e.target.value)} placeholder="Your email subject" style={inputStyle} />
              <button style={{ marginTop: 8, padding: '6px 12px', background: 'rgba(99,102,241,0.1)', color: '#6366f1', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>{'\u2728'} AI Suggest</button>
            </div>
            <div style={{ marginTop: 16 }}>
              <label style={labelStyle}>Preview Text</label>
              <input type="text" value={previewText} onChange={e => setPreviewText(e.target.value)} placeholder="Preview text shown in inbox" style={inputStyle} />
            </div>
          </div>
          <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: 24 }}>
            <h3 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', marginBottom: 16 }}>Email Preview</h3>
            <div style={{ background: '#fff', borderRadius: 8, maxWidth: 600, margin: '0 auto', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              {emailBlocks.map((blockId, i) => {
                const block = CONTENT_BLOCKS.find(b => b.id === blockId);
                return (
                  <div key={i} style={{ padding: blockId === 'divider' ? '8px 24px' : '16px 24px', borderBottom: '1px solid #eee', position: 'relative', minHeight: 40 }}>
                    {blockId === 'header' && <div style={{ background: '#6366f1', padding: '20px 24px', margin: '-16px -24px', color: '#fff', fontSize: 20, fontWeight: 700, textAlign: 'center' }}>Your Business</div>}
                    {blockId === 'text' && <p style={{ color: '#374151', fontSize: 14, lineHeight: 1.6 }}>Your email content goes here. Write compelling copy that engages your audience and drives action.</p>}
                    {blockId === 'image' && <div style={{ background: '#f3f4f6', height: 150, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: 14 }}>Image placeholder</div>}
                    {blockId === 'button' && <div style={{ textAlign: 'center', padding: '8px 0' }}><span style={{ display: 'inline-block', padding: '12px 32px', background: '#6366f1', color: '#fff', borderRadius: 8, fontSize: 14, fontWeight: 600 }}>Call to Action</span></div>}
                    {blockId === 'divider' && <hr style={{ border: 'none', borderTop: '1px solid #e5e7eb', margin: 0 }} />}
                    {blockId === 'social' && <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>Facebook | Twitter | Instagram | LinkedIn</div>}
                    {blockId === 'footer' && <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: 11, padding: '8px 0' }}>You received this email because you subscribed. Unsubscribe</div>}
                    <button onClick={() => removeBlock(i)} style={{ position: 'absolute', top: 4, right: 4, background: 'rgba(239,68,68,0.1)', color: '#ef4444', border: 'none', borderRadius: 4, width: 20, height: 20, cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{'\u2715'}</button>
                  </div>
                );
              })}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'center' }}>
              <button style={{ padding: '10px 24px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>Send Test Email</button>
              <button style={{ padding: '10px 24px', background: '#10b981', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>Schedule Send</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderAutomations = () => (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 600, color: 'var(--text)', marginBottom: 16 }}>Automation Flows</h2>
      <div style={{ display: 'grid', gap: 12 }}>
        {AUTOMATION_FLOWS.map(f => (
          <div key={f.id} style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: 20, display: 'flex', alignItems: 'center', gap: 20 }}>
            <div style={{ flex: 1 }}>
              <h3 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>{f.name}</h3>
              <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Trigger: {f.trigger} | {f.emails} emails in sequence</p>
            </div>
            <div style={{ textAlign: 'center', minWidth: 80 }}>
              <p style={{ fontSize: 20, fontWeight: 700, color: '#6366f1' }}>{f.sent}</p>
              <p style={{ fontSize: 11, color: 'var(--text-muted)' }}>Sent</p>
            </div>
            <span style={{ fontSize: 13, fontWeight: 500, padding: '4px 12px', borderRadius: 6, background: f.status === 'Active' ? 'rgba(16,185,129,0.1)' : 'rgba(245,158,11,0.1)', color: f.status === 'Active' ? '#10b981' : '#f59e0b' }}>{f.status}</span>
            <button style={{ padding: '6px 14px', background: 'transparent', color: '#6366f1', border: '1px solid #6366f1', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>Edit</button>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 24, padding: 20, background: 'var(--card)', border: '1px dashed var(--border)', borderRadius: 12, textAlign: 'center' }}>
        <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 8 }}>Visual Automation Builder</p>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, color: 'var(--text-muted)', fontSize: 13 }}>
          <span style={{ padding: '6px 12px', background: '#10b981', color: '#fff', borderRadius: 6 }}>Trigger</span>
          <span>{'\u2192'}</span>
          <span style={{ padding: '6px 12px', background: 'var(--bg)', borderRadius: 6, border: '1px solid var(--border)' }}>Wait 1 day</span>
          <span>{'\u2192'}</span>
          <span style={{ padding: '6px 12px', background: '#6366f1', color: '#fff', borderRadius: 6 }}>Send Email</span>
          <span>{'\u2192'}</span>
          <span style={{ padding: '6px 12px', background: '#f59e0b', color: '#fff', borderRadius: 6 }}>Condition</span>
          <span>{'\u2192'}</span>
          <span style={{ padding: '6px 12px', background: 'var(--bg)', borderRadius: 6, border: '1px solid var(--border)' }}>Branch</span>
        </div>
      </div>
    </div>
  );

  const renderIntegrations = () => (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 600, color: 'var(--text)', marginBottom: 16 }}>Email Providers</h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
        {PROVIDERS.map(p => (
          <div key={p.id} style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: 20, display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ width: 48, height: 48, borderRadius: 10, background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 700, color: '#6366f1' }}>{p.name[0]}</div>
            <div style={{ flex: 1 }}>
              <h4 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>{p.name}</h4>
              <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>Ready to connect</p>
            </div>
            <button style={{ padding: '6px 14px', background: 'transparent', color: '#6366f1', border: '1px solid #6366f1', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>Connect</button>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 28, fontWeight: 700, color: 'var(--text)', marginBottom: 6 }}>{'\uD83D\uDCE7'} Email Marketing</h1>
        <p style={{ color: 'var(--text-muted)', fontSize: 15, maxWidth: 600 }}>AI-powered email campaigns, automation flows, and subscriber management.</p>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        {['campaigns', 'editor', 'automations', 'integrations'].map(t => (
          <button key={t} onClick={() => { setTab(t); if (t === 'editor') setSelectedType(null); }} style={{ padding: '8px 20px', background: tab === t ? '#6366f1' : 'var(--card)', color: tab === t ? '#fff' : 'var(--text-muted)', border: tab === t ? 'none' : '1px solid var(--border)', borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: 'pointer', textTransform: 'capitalize' }}>{t}</button>
        ))}
      </div>

      {tab === 'campaigns' && renderCampaigns()}
      {tab === 'editor' && renderEditor()}
      {tab === 'automations' && renderAutomations()}
      {tab === 'integrations' && renderIntegrations()}
    </div>
  );
}

const labelStyle = { display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6 };
const inputStyle = { width: '100%', padding: '10px 14px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)', fontSize: 14, outline: 'none', boxSizing: 'border-box' };

export default EmailMarketingPage;
